import { useState, useEffect } from "react";
import {
  Search,
  Eye,
  RefreshCw,
  Mail,
  Phone,
  FileText,
  Briefcase,
  MapPin,
  X,
  ExternalLink,
  FileCheck,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_career_data,
  update_career_status,
  APL_LINK,
} from "../serviceconnection/ServiceConnection";

// ─────────────────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  0: {
    label: "Pending",
    bg: "hsl(var(--muted))",
    text: "hsl(var(--muted-foreground))",
  },
  1: {
    label: "Accepted",
    bg: "hsl(var(--info-muted))",
    text: "hsl(var(--info))",
  },
  2: {
    label: "Shortlisted",
    bg: "hsl(var(--accent)/0.12)",
    text: "hsl(var(--accent))",
  },
  3: {
    label: "Arrived",
    bg: "hsl(var(--success-muted))",
    text: "hsl(var(--success))",
  },
  4: {
    label: "Change Time",
    bg: "hsl(var(--warning-muted))",
    text: "hsl(var(--warning))",
  },
  5: {
    label: "Selected",
    bg: "hsl(var(--success-muted))",
    text: "hsl(var(--success))",
  },
  6: {
    label: "Not Selected",
    bg: "hsl(var(--destructive-muted))",
    text: "hsl(var(--destructive))",
  },
  7: {
    label: "Rejected",
    bg: "hsl(var(--destructive-muted))",
    text: "hsl(var(--destructive))",
  },
  8: {
    label: "No Reporting",
    bg: "hsl(var(--warning-muted))",
    text: "hsl(var(--warning))",
  },
  9: {
    label: "Reported",
    bg: "hsl(var(--success-muted))",
    text: "hsl(var(--success))",
  },
};

// Status options available for update (what admin can set)
const STATUS_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const StatusBadge = ({ val }) => {
  const cfg = STATUS_CONFIG[val] ?? STATUS_CONFIG[0];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.text }}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {cfg.label}
    </span>
  );
};

// Which extra fields each status needs
const STATUS_EXTRA_FIELDS = {
  1: ["comment_reason"],
  2: [
    "arrived_date",
    "arrived_time",
    "mode_interview",
    "link_interview",
    "comment_reason",
  ],
  4: ["arrived_date", "arrived_time", "comment_reason"],
  5: ["join_date", "join_time", "salary_amount", "report_to", "comment_reason"],
  6: ["comment_reason"],
  7: ["comment_reason"],
  8: ["comment_reason"],
};

const EMPTY_STATUS_FORM = {
  status_job: "",
  comment_reason: "",
  arrived_date: "",
  arrived_time: "",
  join_date: "",
  join_time: "",
  mode_interview: "Online",
  link_interview: "",
  salary_amount: "",
  report_to: "",
};

// ─────────────────────────────────────────────────────────────────────────────
export default function Careers() {
  const [careers, setCareers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [listLoading, setListLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Detail modal
  const [viewCareer, setViewCareer] = useState(null);

  // Update status modal
  const [updateModal, setUpdateModal] = useState(false);
  const [activeCareer, setActiveCareer] = useState(null);
  const [statusForm, setStatusForm] = useState(EMPTY_STATUS_FORM);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = careers.filter((c) => {
    const matchSearch =
      c.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.job_email?.toLowerCase().includes(search.toLowerCase()) ||
      c.designation?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "All" ||
      STATUS_CONFIG[c.status_job]?.label === statusFilter;
    return matchSearch && matchStatus;
  });

  // Stats per status (top cards)
  const statCounts = {
    total: careers.length,
    pending: careers.filter((c) => c.status_job === 0).length,
    shortlisted: careers.filter((c) => c.status_job === 2).length,
    selected: careers.filter((c) => c.status_job === 5).length,
  };

  // ── Fetch List ──────────────────────────────────────────────────────────────
  const fetchCareers = async () => {
    try {
      setListLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      fd.append("status_job", -1);
      const res = await server_post_data(get_career_data, fd);
      setCareers(res.data?.data ?? []);
    } catch {
      showError("Failed to load applications");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchCareers();
  }, []);

  // ── View Detail ─────────────────────────────────────────────────────────────
  const openDetail = async (c) => {
    try {
      const fd = new FormData();
      fd.append("main_id", c.primary_id);
      const res = await server_post_data(get_career_data, fd);
      setViewCareer(res.data?.data ?? c);
    } catch {
      setViewCareer(c); // fallback to list data
    }
  };

  // ── Open Update Status Modal ────────────────────────────────────────────────
  const openUpdateModal = (c) => {
    setActiveCareer(c);
    setStatusForm({ ...EMPTY_STATUS_FORM, status_job: c.status_job });
    setUpdateModal(true);
  };

  // ── Submit Status Update ────────────────────────────────────────────────────
  const handleStatusUpdate = async () => {
    if (statusForm.status_job === "" || statusForm.status_job === null) {
      showError("Please select a status");
      return;
    }
    try {
      setUpdateLoading(true);
      const fd = new FormData();
      fd.append("p_id", activeCareer.primary_id);
      Object.entries(statusForm).forEach(([k, v]) => {
        if (v !== "") fd.append(k, v);
      });
      const res = await server_post_data(update_career_status, fd);
      if (res.data?.error) {
        showError(res.data?.message || "Update failed");
        return;
      }
      showSuccess(res.data?.message ?? "Status updated");
      setUpdateModal(false);
      // Optimistic update
      setCareers((prev) =>
        prev.map((c) =>
          c.primary_id === activeCareer.primary_id
            ? { ...c, status_job: parseInt(statusForm.status_job) }
            : c,
        ),
      );
    } catch {
      showError("Something went wrong");
    } finally {
      setUpdateLoading(false);
    }
  };

  const extraFields =
    STATUS_EXTRA_FIELDS[parseInt(statusForm.status_job)] ?? [];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Career Applications</h1>
          <p className="page-subtitle">
            Review and manage job applications received from the website
          </p>
        </div>
        <button
          onClick={fetchCareers}
          className="flex items-center gap-2 text-sm font-semibold border border-border rounded-lg px-4 py-2 text-foreground hover:bg-muted transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Total Applications",
            value: statCounts.total,
            color: "hsl(var(--accent))",
          },
          {
            label: "Pending Review",
            value: statCounts.pending,
            color: "hsl(var(--warning))",
          },
          {
            label: "Shortlisted",
            value: statCounts.shortlisted,
            color: "hsl(var(--info))",
          },
          {
            label: "Selected",
            value: statCounts.selected,
            color: "hsl(var(--success))",
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="cms-card p-4 flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or designation..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            "All",
            "Pending",
            "Accepted",
            "Shortlisted",
            "Selected",
            "Rejected",
          ].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                statusFilter === s
                  ? "text-white border-transparent"
                  : "border-border text-muted-foreground hover:border-accent/30"
              }`}
              style={
                statusFilter === s ? { background: "hsl(var(--accent))" } : {}
              }
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="section-panel">
        <div className="overflow-x-auto">
          <table className="w-full cms-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Applied For</th>
                <th>Contact</th>
                <th>Applied On</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    No applications found
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.primary_id} className="group">
                    {/* Applicant */}
                    <td>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ background: "var(--gradient-hero)" }}
                        >
                          {c.user_name
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-foreground text-sm">
                            {c.user_name}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {c.job_email}
                          </p>
                        </div>
                      </div>
                    </td>
                    {/* Applied For */}
                    <td>
                      <span className="flex items-center gap-1.5 text-sm text-foreground font-medium">
                        <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
                        {c.designation ?? `Vacancy #${c.vacancy_id}`}
                      </span>
                    </td>
                    {/* Contact */}
                    <td>
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Phone className="w-3.5 h-3.5" />
                        {c.job_mobile_no}
                      </span>
                    </td>
                    {/* Date */}
                    <td className="text-sm text-muted-foreground">
                      {c.entry_date?.slice(0, 10)}
                    </td>
                    {/* Status */}
                    <td>
                      <StatusBadge val={c.status_job} />
                    </td>
                    {/* Actions */}
                    <td>
                      {c.status_job !== 6 && c.status_job !== 7 && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openDetail(c)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openUpdateModal(c)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                            title="Update Status"
                            style={{ color: "hsl(var(--accent))" }}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {careers.length} applications
          </p>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          VIEW DETAIL MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      {viewCareer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={() => setViewCareer(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-lg max-h-[90vh] overflow-y-auto fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header sticky top-0 z-10">
              <h2 className="section-panel-title">Application Details</h2>
              <button
                onClick={() => setViewCareer(null)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="section-panel-body space-y-4">
              {/* Applicant Header */}
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                  style={{ background: "var(--gradient-hero)" }}
                >
                  {viewCareer.user_name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    {viewCareer.user_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {viewCareer.job_email}
                  </p>
                </div>
                <div className="ml-auto">
                  <StatusBadge val={viewCareer.status_job} />
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["Phone", viewCareer.job_mobile_no],
                  [
                    "Applied For",
                    viewCareer.vacancy_details?.designation ??
                      `Vacancy #${viewCareer.vacancy_id}`,
                  ],
                  ["Location", viewCareer.vacancy_details?.loaction ?? "—"],
                  [
                    "Experience",
                    viewCareer.vacancy_details
                      ? `${viewCareer.vacancy_details.min_experience}–${viewCareer.vacancy_details.max_experience} yrs`
                      : "—",
                  ],
                  ["Applied On", viewCareer.entry_date?.slice(0, 10)],
                  ["Mode", viewCareer.mode_interview ?? "—"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="p-3 rounded-lg bg-muted/40 border border-border"
                  >
                    <p className="text-xs text-muted-foreground mb-0.5">{k}</p>
                    <p className="text-sm font-semibold text-foreground">
                      {v ?? "—"}
                    </p>
                  </div>
                ))}
              </div>

              {/* Message */}
              {viewCareer.job_message && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    Cover Message
                  </p>
                  <p className="text-sm text-foreground bg-muted/40 rounded-lg p-3 border border-border">
                    {viewCareer.job_message}
                  </p>
                </div>
              )}

              {/* Comment / Reason */}
              {viewCareer.comment_reason && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    Admin Note
                  </p>
                  <p className="text-sm text-foreground bg-muted/40 rounded-lg p-3 border border-border">
                    {viewCareer.comment_reason}
                  </p>
                </div>
              )}

              {/* Interview Details */}
              {(viewCareer.arrived_date || viewCareer.join_date) && (
                <div className="grid grid-cols-2 gap-3">
                  {viewCareer.arrived_date && (
                    <>
                      <div className="p-3 rounded-lg bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground mb-0.5">
                          Interview Date
                        </p>
                        <p className="text-sm font-semibold text-foreground">
                          {viewCareer.arrived_date}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground mb-0.5">
                          Interview Time
                        </p>
                        <p className="text-sm font-semibold text-foreground">
                          {viewCareer.arrived_time ?? "—"}
                        </p>
                      </div>
                    </>
                  )}
                  {viewCareer.join_date && (
                    <>
                      <div className="p-3 rounded-lg bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground mb-0.5">
                          Joining Date
                        </p>
                        <p className="text-sm font-semibold text-foreground">
                          {viewCareer.join_date}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/40 border border-border">
                        <p className="text-xs text-muted-foreground mb-0.5">
                          Salary Offered
                        </p>
                        <p className="text-sm font-semibold text-foreground">
                          {viewCareer.salary_amount
                            ? `₹${viewCareer.salary_amount.toLocaleString("en-IN")}`
                            : "—"}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* LinkedIn */}
              {viewCareer.linkdine_profile && (
                <a
                  href={viewCareer.linkdine_profile}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-foreground"
                >
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />{" "}
                  LinkedIn Profile
                </a>
              )}

              {/* Resume + Cover */}
              <div className="flex gap-3">
                {viewCareer.resume_url && (
                  <a
                    href={APL_LINK + viewCareer.resume_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg text-white transition-colors"
                    style={{ background: "hsl(var(--accent))" }}
                  >
                    <FileText className="w-4 h-4" /> View Resume
                  </a>
                )}
                {viewCareer.cover_url && (
                  <a
                    href={APL_LINK + viewCareer.cover_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    <FileCheck className="w-4 h-4" /> Cover Letter
                  </a>
                )}
              </div>

              {/* Update Status shortcut */}
              {viewCareer.status_job !== 6 && viewCareer.status_job !== 7 && (
                <button
                  onClick={() => openUpdateModal(viewCareer)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                  title="Update Status"
                  style={{ color: "hsl(var(--accent))" }}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          UPDATE STATUS MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      {updateModal && activeCareer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={() => setUpdateModal(false)}
        >
          <div
            className="section-panel p-0 w-full max-w-md max-h-[90vh] overflow-y-auto fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header sticky top-0 z-10">
              <h2 className="section-panel-title">Update Application Status</h2>
              <button
                onClick={() => setUpdateModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="section-panel-body space-y-4">
              {/* Applicant Quick Info */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ background: "var(--gradient-hero)" }}
                >
                  {activeCareer.user_name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">
                    {activeCareer.user_name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {activeCareer.designation ??
                      `Vacancy #${activeCareer.vacancy_id}`}
                  </p>
                </div>
                <StatusBadge val={activeCareer.status_job} />
              </div>

              {/* Status Select */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  New Status <span className="text-destructive">*</span>
                </label>
                <select
                  value={statusForm.status_job}
                  onChange={(e) =>
                    setStatusForm({
                      ...EMPTY_STATUS_FORM,
                      status_job: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                >
                  <option value="" disabled>
                    Select status...
                  </option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s} — {STATUS_CONFIG[s].label}
                    </option>
                  ))}
                </select>
              </div>

              {/* ── Conditional Extra Fields ── */}

              {/* Interview Schedule (status 2 & 4) */}
              {extraFields.includes("arrived_date") && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground">
                      Interview Date
                    </label>
                    <input
                      type="date"
                      value={statusForm.arrived_date}
                      onChange={(e) =>
                        setStatusForm((p) => ({
                          ...p,
                          arrived_date: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground">
                      Interview Time
                    </label>
                    <input
                      type="time"
                      value={statusForm.arrived_time}
                      onChange={(e) =>
                        setStatusForm((p) => ({
                          ...p,
                          arrived_time: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                    />
                  </div>
                </div>
              )}

              {/* Mode + Link (status 2) */}
              {extraFields.includes("mode_interview") && (
                <>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-muted-foreground">
                      Interview Mode
                    </label>
                    <select
                      value={statusForm.mode_interview}
                      onChange={(e) =>
                        setStatusForm((p) => ({
                          ...p,
                          mode_interview: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                    >
                      <option value="Online">Online</option>
                      <option value="Offline">Offline (In-Person)</option>
                    </select>
                  </div>
                  {statusForm.mode_interview === "Online" && (
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-muted-foreground">
                        Interview Link
                      </label>
                      <input
                        type="url"
                        value={statusForm.link_interview}
                        onChange={(e) =>
                          setStatusForm((p) => ({
                            ...p,
                            link_interview: e.target.value,
                          }))
                        }
                        placeholder="https://meet.google.com/..."
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Joining Details (status 5) */}
              {extraFields.includes("join_date") && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-muted-foreground">
                        Joining Date
                      </label>
                      <input
                        type="date"
                        value={statusForm.join_date}
                        onChange={(e) =>
                          setStatusForm((p) => ({
                            ...p,
                            join_date: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-muted-foreground">
                        Joining Time
                      </label>
                      <input
                        type="time"
                        value={statusForm.join_time}
                        onChange={(e) =>
                          setStatusForm((p) => ({
                            ...p,
                            join_time: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-muted-foreground">
                        Salary Offered (₹)
                      </label>
                      <input
                        type="number"
                        value={statusForm.salary_amount}
                        onChange={(e) =>
                          setStatusForm((p) => ({
                            ...p,
                            salary_amount: e.target.value,
                          }))
                        }
                        placeholder="e.g. 35000"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-muted-foreground">
                        Report To
                      </label>
                      <input
                        type="text"
                        value={statusForm.report_to}
                        onChange={(e) =>
                          setStatusForm((p) => ({
                            ...p,
                            report_to: e.target.value,
                          }))
                        }
                        placeholder="e.g. Mr. Sharma"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Comment Reason */}
              {extraFields.includes("comment_reason") && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    {parseInt(statusForm.status_job) === 1
                      ? "Acceptance Note"
                      : parseInt(statusForm.status_job) === 5
                        ? "Additional Note"
                        : "Reason / Comment"}
                  </label>
                  <textarea
                    value={statusForm.comment_reason}
                    onChange={(e) =>
                      setStatusForm((p) => ({
                        ...p,
                        comment_reason: e.target.value,
                      }))
                    }
                    rows={3}
                    placeholder="Add a note or reason..."
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setUpdateModal(false)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={updateLoading}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ background: "hsl(var(--accent))" }}
                >
                  {updateLoading ? "Updating..." : "Update Status"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
